/**
 * Shared animation presets and utilities for Praevue video templates.
 *
 * All motion should support clarity, not excitement.
 * Smooth fades, slides, scale-ins, and simple draws only.
 */
import { Easing, interpolate, spring } from "remotion";

// ── Spring presets ──────────────────────────────────────────
export const springs = {
  /** Gentle entrance — cards, labels */
  gentle: { damping: 26, stiffness: 100 },
  /** Snappy entrance — badges, pills */
  snappy: { damping: 28, stiffness: 135 },
  /** Soft settle — large text, numbers */
  settle: { damping: 24, stiffness: 90 },
  /** Controlled rise — CTA, result cards */
  rise: { damping: 24, stiffness: 92 },
  /** Quick pop — accent elements */
  pop: { damping: 20, stiffness: 160 },
};

// ── Fade helper ─────────────────────────────────────────────
export function fade(frame, startFrame = 0, duration = 18) {
  return interpolate(frame, [startFrame, startFrame + duration], [0, 1], {
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp",
  });
}

/** Convert milliseconds to frames at a given fps. */
export function msToFrames(ms, fps) {
  return Math.max(1, Math.round((ms / 1000) * fps));
}

/**
 * Fade in per screen guide: easeOut for entrance (opacity only — flat fade).
 * @param {number} durationMs - default 250
 */
export function fadeInEaseOut(frame, startFrame, fps, durationMs = 250) {
  const d = msToFrames(durationMs, fps);
  return interpolate(frame, [startFrame, startFrame + d], [0, 1], {
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp",
    easing: Easing.out(Easing.ease),
  });
}

/**
 * Fade **up** entrance: same easing as {@link fadeInEaseOut}, plus translateY from `offsetPx` → 0.
 * Matches `docs/remotion/template-code.js` useEntrance (`translateY(14px)` → `translateY(0)`).
 *
 * Spread into `style`: `style={{ ...fadeUpEaseOut(frame, 0, fps, 300, s(14)), fontSize: … }}`
 *
 * @param {number} offsetPx - downward start offset in **pixels** (pass `s(14)` when using layout scale)
 */
export function fadeUpEaseOut(frame, startFrame, fps, durationMs = 250, offsetPx = 14) {
  const t = fadeInEaseOut(frame, startFrame, fps, durationMs);
  const y = interpolate(t, [0, 1], [offsetPx, 0], {
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp",
  });
  return {
    opacity: t,
    transform: `translateY(${y}px)`,
  };
}

/** Fade out: easeIn */
export function fadeOutEaseIn(frame, startFrame, fps, durationMs = 200) {
  const d = msToFrames(durationMs, fps);
  return interpolate(frame, [startFrame, startFrame + d], [0, 1], {
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp",
    easing: Easing.in(Easing.ease),
  });
}

/**
 * Opacity for a scene window: fade in at start, fade out at end (hold in between).
 */
export function sceneFadeWindow(
  frame,
  durationInFrames,
  fps,
  { fadeInMs = 250, fadeOutMs = 200, holdBeforeOutFrames = 0 } = {}
) {
  const inF = msToFrames(fadeInMs, fps);
  const outF = msToFrames(fadeOutMs, fps);
  const outStart = Math.max(inF, durationInFrames - outF - holdBeforeOutFrames);
  const inOp = fadeInEaseOut(frame, 0, fps, fadeInMs);
  const outOp = fadeOutEaseIn(frame, outStart, fps, fadeOutMs);
  if (frame < outStart) return inOp;
  return interpolate(frame, [outStart, outStart + outF], [1, 0], {
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp",
    easing: Easing.in(Easing.ease),
  });
}

// ── Slide-in from direction ─────────────────────────────────
export function slideIn(frame, fps, { delay = 0, from = 60, direction = "up", config = springs.settle } = {}) {
  const progress = spring({ frame: frame - delay, fps, config });
  const axis = direction === "up" || direction === "down" ? "Y" : "X";
  const sign = direction === "down" || direction === "right" ? -1 : 1;
  const offset = interpolate(progress, [0, 1], [from * sign, 0]);
  const opacity = fade(frame, delay, 18);

  return {
    transform: `translate${axis}(${offset}px)`,
    opacity,
  };
}

// ── Scale-in ────────────────────────────────────────────────
export function scaleIn(frame, fps, { delay = 0, from = 0.9, config = springs.gentle } = {}) {
  const progress = spring({ frame: frame - delay, fps, config });
  const scale = interpolate(progress, [0, 1], [from, 1]);
  const opacity = fade(frame, delay, 16);

  return {
    transform: `scale(${scale})`,
    opacity,
  };
}

// ── Bar scale (vertical grow) ───────────────────────────────
export function barGrow(frame, fps, { delay = 6, config = springs.pop } = {}) {
  const progress = spring({ frame: frame - delay, fps, config });
  return interpolate(progress, [0, 1], [0, 1]);
}

// ── Stagger helper — returns delay for item at index ────────
export function stagger(index, baseDelay = 0, interval = 8) {
  return baseDelay + index * interval;
}

function wordCount(text) {
  return String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function normalizeAlignmentWord(raw) {
  if (!raw) return null;
  if (typeof raw === "string") {
    return { word: raw, startMs: null, endMs: null };
  }
  const word = String(raw.word ?? raw.text ?? raw.token ?? "").trim();
  if (!word) return null;
  const startMs = Number(raw.startMs ?? raw.start ?? raw.start_time ?? NaN);
  const endMs = Number(raw.endMs ?? raw.end ?? raw.end_time ?? NaN);
  return {
    word,
    startMs: Number.isFinite(startMs) ? startMs : null,
    endMs: Number.isFinite(endMs) ? endMs : null,
  };
}

function normalizeToken(token) {
  return String(token || "")
    .toLowerCase()
    .replace(/(^[^a-z0-9$%+-]+|[^a-z0-9$%+-]+$)/gi, "");
}

function tokenize(text) {
  return String(text || "")
    .split(/\s+/)
    .map(normalizeToken)
    .filter(Boolean);
}

/**
 * Estimate narration length in frames for a section.
 * Prefers TTS alignment words when present, otherwise falls back to WPM estimate.
 */
export function estimateSectionReadFrames({
  text = "",
  tts = null,
  fps = 30,
  fallbackWpm = 155,
  minReadMs = 1000,
} = {}) {
  const fallbackFrames = msToFrames(
    Math.max(minReadMs, (wordCount(text) / Math.max(1, fallbackWpm)) * 60_000),
    fps
  );

  const alignmentWords = Array.isArray(tts?.alignment?.words) ? tts.alignment.words : null;
  if (!alignmentWords || alignmentWords.length === 0) return fallbackFrames;

  const normalizedWords = alignmentWords.map(normalizeAlignmentWord).filter(Boolean);
  const sectionTokens = tokenize(text);
  if (sectionTokens.length === 0 || normalizedWords.length === 0) return fallbackFrames;

  let firstIdx = -1;
  let lastIdx = -1;
  let cursor = 0;
  for (const token of sectionTokens) {
    let hit = -1;
    for (let i = cursor; i < normalizedWords.length; i++) {
      if (normalizeToken(normalizedWords[i].word) === token) {
        hit = i;
        break;
      }
    }
    if (hit === -1) continue;
    if (firstIdx === -1) firstIdx = hit;
    lastIdx = hit;
    cursor = hit + 1;
  }

  if (firstIdx === -1 || lastIdx === -1) return fallbackFrames;

  const startMs = normalizedWords[firstIdx].startMs;
  const endMs = normalizedWords[lastIdx].endMs ?? normalizedWords[lastIdx].startMs;
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return fallbackFrames;
  }

  return msToFrames(Math.max(minReadMs, endMs - startMs), fps);
}

/**
 * Compute canonical scene timing windows for entrance/read/pause/exit flow.
 */
export function buildSceneTiming({
  fps,
  readFrames,
  entranceMs = 420,
  pauseMs = 300,
  exitMs = 260,
} = {}) {
  const entranceFrames = msToFrames(entranceMs, fps);
  const pauseFrames = msToFrames(pauseMs, fps);
  const exitFrames = msToFrames(exitMs, fps);
  const safeReadFrames = Math.max(1, Math.floor(Number(readFrames) || 1));

  const enterEndFrame = entranceFrames;
  const readStartFrame = enterEndFrame;
  const readEndFrame = readStartFrame + safeReadFrames;
  const pauseEndFrame = readEndFrame + pauseFrames;
  const exitStartFrame = pauseEndFrame;
  const exitEndFrame = exitStartFrame + exitFrames;

  return {
    entranceFrames,
    pauseFrames,
    exitFrames,
    enterEndFrame,
    readStartFrame,
    readEndFrame,
    pauseEndFrame,
    exitStartFrame,
    exitEndFrame,
    minDurationInFrames: exitEndFrame,
  };
}

/**
 * Return standardized scene exit transform + opacity for a local scene frame.
 */
export function getSceneExitWindowStyle(
  frame,
  sceneTiming,
  { distancePx = 18, direction = "up" } = {}
) {
  if (!sceneTiming) {
    return { opacity: 1, transform: "translate3d(0, 0, 0)" };
  }
  const start = Math.max(0, Number(sceneTiming.exitStartFrame) || 0);
  const end = Math.max(start + 1, Number(sceneTiming.exitEndFrame) || start + 1);
  const progress = interpolate(frame, [start, end], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.inOut(Easing.ease),
  });

  const sign = direction === "down" || direction === "right" ? 1 : -1;
  const axis = direction === "left" || direction === "right" ? "X" : "Y";
  const offset = distancePx * sign * progress;
  const opacity = interpolate(progress, [0, 1], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return {
    opacity,
    transform: `translate${axis}(${offset}px)`,
  };
}
